import { Divider, Group, Select } from '@byecode/ui'
import type { TypeInstanceMap, UpdatePageStateAction } from '@lighthouse/core'
import type { NodeTypes } from '@lighthouse/shared'
import { FieldTypeTag, ListItem4ByecodeUi, nodeTypeOptions, useAtomData } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom, pageStatesParameterFamily } from '@/atoms/page/state'
import { ArgInput } from '@/components/ArgInput'

import * as SC from '../styles'

interface UpdatePageStateActionConfigureProps {
    nodeType?: NodeTypes
    actionTypeSwitcher?: React.ReactNode
    allParentNodes?: import('@lighthouse/shared').FlowNode[]
    allParentActions?: import('@lighthouse/core').IndependentAction[]
    value: UpdatePageStateAction['data']['updatePageState']
    onChange: (value: UpdatePageStateAction['data']['updatePageState']) => void
}

export const UpdatePageStateActionConfigure: React.FC<UpdatePageStateActionConfigureProps> = ({
    actionTypeSwitcher,
    nodeType,
    allParentNodes,
    allParentActions,
    value,
    onChange
}) => {
    const { pageId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(
            s => ({
                pageId: s?.pageId ?? ''
            }),
            []
        )
    )
    const { pageStateId } = value
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))

    const currentPageState = useMemo(() => {
        return pageStateParameter?.find(item => item.id === pageStateId)
    }, [pageStateParameter, pageStateId])

    const options = useMemo(() => {
        if (!pageStateParameter) {
            return []
        }
        return pageStateParameter.map(item => ({
            label: item.name,
            value: item.id,
            type: item.innerType,
            extra: <FieldTypeTag innerType={item.innerType} />
        }))
    }, [pageStateParameter])

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>目标更新变量</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select
                            searchable
                            options={options}
                            styles={{
                                root: {
                                    alignItems: 'center'
                                },
                                prefix: {
                                    lineHeight: 'normal',
                                    height: 20,
                                    margin: '0 4px'
                                }
                            }}
                            prefix={
                                pageStateId && (
                                    <FieldTypeTag innerType={options.find(item => item.value === pageStateId)?.type} />
                                )
                            }
                            value={pageStateId}
                            onChange={val => {
                                onChange({ ...value, pageStateId: val })
                            }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            {pageStateId && currentPageState && (
                <>
                    <Divider />
                    <Group label="变量值更新为">
                        <ListItem4ByecodeUi>
                            <SC.FormItemContent style={{ flex: 1 }}>
                                <ArgInput
                                    autoHeight
                                    disabledLabel
                                    data={currentPageState}
                                    allParentNodes={allParentNodes}
                                    allParentActions={allParentActions}
                                    value={value.value}
                                    onChange={v => {
                                        onChange({ ...value, value: v })
                                    }}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>
                    </Group>
                </>
            )}
        </SC.Container>
    )
}
